/**
 * Sentry Error Tracking Service
 * 
 * Centralizes Sentry initialization and configuration for:
 * - Client app (web + Capacitor iOS/Android)
 * - Admin panel
 * - Edge functions (via Sentry Deno SDK)
 * 
 * GDPR Compliance: PII auto-capture disabled via beforeSend hook
 */

import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { logger } from '@deskbay/shared';
import { isKnownThirdPartyNoise } from './sentryNoiseFilter';

interface SentryConfig {
  dsn: string;
  environment: string;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
}

/**
 * Initialize Sentry for client app
 * Call this BEFORE React.render() in index.tsx
 */
export function initSentry(config: SentryConfig) {
  // Skip if DSN not configured (dev mode)
  if (!config.dsn || config.dsn.includes('xxxxx')) {
    logger.error.info('Sentry DSN not configured, skipping initialization', 'sentry.initSentry');
    return;
  }

  const platform = Capacitor.getPlatform();
  const isNative = platform === 'ios' || platform === 'android';

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    
    // Performance Monitoring
    tracesSampleRate: config.tracesSampleRate,
    
    // Session Replay
    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
    
    integrations: [
      // Browser tracing for web performance
      Sentry.browserTracingIntegration(),
      
      // Session replay for debugging UX issues
      Sentry.replayIntegration({
        maskAllText: true, // GDPR: mask all text by default
        blockAllMedia: true, // GDPR: block images/video
      }),
      
      // React Router integration
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),

      // Drop errors that originate EXCLUSIVELY from third-party code — browser
      // extensions, injected GTM/gtag scripts, wallet/Firefox scripts, etc. Relies on
      // the `applicationKey: 'deskbay-app'` set in the Sentry Vite plugin to mark our
      // frames: an event is only dropped if NONE of its frames carry that key, so an
      // error with even one of our frames (e.g. #3 supabase-js lock, which shares a
      // frame with our bundle) is never touched. Verified in TAG mode on Sentry
      // (07/08/2026): 105/105 RangeError + 52/52 webkit.messageHandlers events tagged
      // `third_party_code:true`, while the known real-frame case (#3) was correctly
      // NOT tagged. See .docs/SENTRY_ISSUES_TRACKING.md.
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ['deskbay-app'],
        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
      }),
    ],
    
    // Release tracking (auto-populated by Vite plugin)
    release: import.meta.env.VITE_SENTRY_RELEASE,
    
    // GDPR Compliance: scrub PII before sending
    beforeSend(event, hint) {
      // ── Noise filter (deterministic allowlist) ─────────────────────────────
      // Drop confidently-identified third-party / benign errors BEFORE the
      // DeskbayLogger call below, so they pollute neither Sentry nor app_logs.
      // Fail-safe: anything not matched is kept. See services/sentryNoiseFilter.ts.
      const noiseMessage = event.message || event.exception?.values?.[0]?.value;
      const noiseFrames = (event.exception?.values ?? []).flatMap(
        value => value.stacktrace?.frames?.map(frame => frame.filename) ?? []
      );
      if (isKnownThirdPartyNoise(noiseMessage, noiseFrames)) {
        return null;
      }

      // Remove sensitive data from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
          // Remove auth tokens from HTTP requests
          if (breadcrumb.data?.url) {
            breadcrumb.data.url = breadcrumb.data.url.replace(/Bearer [^\s]+/g, 'Bearer [REDACTED]');
          }
          return breadcrumb;
        });
      }
      
      // Remove email/phone from user context (keep only ID)
      if (event.user) {
        delete event.user.email;
        delete event.user.ip_address;
      }
      
      // Log to DeskbayLogger for cross-reference
      logger.error.error(
        `Sentry event captured: ${event.message || event.exception?.values?.[0]?.type}`,
        hint.originalException,
        'sentry.beforeSend',
        { eventId: event.event_id }
      );
      
      return event;
    },
    
    // Ignore common non-critical errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      
      // Network errors (handled by app)
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      
      // Capacitor native errors (handled separately)
      'AuthSessionMissingError',
      
      // React hot reload (dev only)
      'ResizeObserver loop limit exceeded',
    ],
    
    // Platform-specific config
    ...(isNative && {
      // Native: disable session replay (not supported)
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      
      // Native: add device context
      initialScope: {
        tags: {
          platform,
          capacitor_version: Capacitor.getPlatform(),
        },
      },
    }),
  });
  
  logger.error.info(`Sentry initialized (${config.environment}, platform: ${platform})`, 'sentry.initSentry');
}

/**
 * Set user context for error tracking
 * Call after successful login
 */
export function setSentryUser(userId: string, restaurantId?: string) {
  Sentry.setUser({
    id: userId,
    // GDPR: do NOT include email/name
  });
  
  if (restaurantId) {
    Sentry.setTag('restaurant_id', restaurantId);
  }
  
  logger.error.debug('Sentry user context set', 'sentry.setSentryUser', { userId, restaurantId });
}

/**
 * Clear user context on logout
 */
export function clearSentryUser() {
  Sentry.setUser(null);
  logger.error.debug('Sentry user context cleared', 'sentry.clearSentryUser');
}

/**
 * Manually capture exception
 * Use for critical errors that need immediate attention
 */
export function captureSentryException(error: unknown, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture message (non-error event)
 * Use for important business events
 */
export function captureSentryMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
  Sentry.captureMessage(message, level);
}

/**
 * Add breadcrumb for debugging context
 * Automatically captured by Sentry, but can be manually added
 */
export function addSentryBreadcrumb(message: string, category: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data,
  });
}

/**
 * React Error Boundary wrapper
 * Use in App.tsx to catch React component errors
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * Get Sentry config from env vars
 */
export function getSentryConfig(): SentryConfig {
  return {
    dsn: import.meta.env.VITE_SENTRY_DSN || '',
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
    replaysSessionSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.1'),
    replaysOnErrorSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0'),
  };
}
